// SnoozeMenu — popover with 4 preset snooze durations + a custom date/time picker.
//
// Opens next to the StatusPill. Parent controls visibility (`open`/`onClose`)
// because two entry points — the StatusPill "Snoozed" click and the Z
// keyboard shortcut — need to toggle the same popover.
//
// On select, fires useUpdateStatus({status:'snoozed', snoozed_until}) and
// calls onClose() after the mutation kicks off. The mutation invalidates the
// conversation + Standup list so the UI updates.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SNOOZE_PRESETS } from '@md/shared';
import { useUpdateStatus } from '@/lib/hooks/useConversation';
type SnoozeMenuProps = {
  conversationId: string;
  open: boolean;
  onClose: () => void;
};
/** Default custom-picker value = tomorrow 9 AM local time, formatted for <input type="datetime-local">. */
function tomorrowAt9Local(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  // yyyy-MM-ddTHH:mm
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export function SnoozeMenu({ conversationId, open, onClose }: SnoozeMenuProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<'presets' | 'custom'>('presets');
  const [customValue, setCustomValue] = useState<string>(tomorrowAt9Local);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateStatus();
  const navigate = useNavigate();
  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, onClose]);
  // Reset to preset mode whenever the popover opens fresh.
  useEffect(() => {
    if (open) {
      setMode('presets');
      setError(null);
    }
  }, [open]);
  if (!open) return null;
  const fireSnooze = (until: Date) => {
    setError(null);
    update.mutate(
      {
        id: conversationId,
        status: 'snoozed',
        snoozed_until: until.toISOString(),
      },
      {
        onSuccess: () => {
          onClose();
          navigate('/');
        },
        onError: (err) => setError(err.message),
      },
    );
  };
  const handlePreset = (minutes: number) => {
    const until = new Date(Date.now() + minutes * 60_000);
    fireSnooze(until);
  };
  const handleCustomSave = () => {
    if (!customValue) {
      setError('Pick a date and time.');
      return;
    }
    const until = new Date(customValue);
    if (Number.isNaN(until.getTime())) {
      setError("That doesn't look like a valid date.");
      return;
    }
    if (until.getTime() <= Date.now()) {
      setError('Snooze time must be in the future.');
      return;
    }
    fireSnooze(until);
  };
  return (
    <div
      ref={wrapperRef}
      role="dialog"
      aria-label="Snooze this thread"
      style={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        minWidth: 240,
        background: 'white',
        border: '1px solid hsl(var(--ink) / 0.12)',
        borderRadius: 10,
        boxShadow: '0 8px 24px hsl(var(--ink) / 0.08)',
        padding: 10,
        zIndex: 50,
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          color: 'hsl(var(--ink) / 0.55)',
          padding: '2px 6px 8px',
        }}
      >
        Snooze until
      </div>
      {mode === 'presets' ? (
        <>
          {SNOOZE_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => handlePreset(p.minutes)}
              disabled={update.isPending}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 10px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: 'hsl(var(--ink) / 0.9)',
                cursor: update.isPending ? 'not-allowed' : 'pointer',
                fontSize: 13,
                textAlign: 'left',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = 'hsl(var(--ink) / 0.06)')
              }
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {p.label}
            </button>
          ))}
          <div style={{ height: 1, background: 'hsl(var(--ink) / 0.08)', margin: '6px 0' }} />
          <button
            type="button"
            onClick={() => setMode('custom')}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: 'none',
              background: 'transparent',
              color: 'hsl(var(--ink) / 0.7)',
              cursor: 'pointer',
              fontSize: 13,
              textAlign: 'left',
              fontStyle: 'italic',
            }}
          >
            Pick a date and time…
          </button>
        </>
      ) : (
        <div style={{ padding: '2px 6px' }}>
          <input
            type="datetime-local"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            disabled={update.isPending}
            style={{
              width: '100%',
              padding: '6px 8px',
              borderRadius: 6,
              border: '1px solid hsl(var(--ink) / 0.2)',
              fontSize: 13,
              marginBottom: 8,
            }}
          />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setMode('presets')}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid hsl(var(--ink) / 0.15)',
                background: 'white',
                color: 'hsl(var(--ink) / 0.75)',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleCustomSave}
              disabled={update.isPending}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid hsl(210 70% 55% / 0.45)',
                background: 'hsl(210 70% 55% / 0.12)',
                color: 'hsl(210 70% 40%)',
                fontSize: 12,
                cursor: update.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {update.isPending ? 'Snoozing…' : 'Snooze'}
            </button>
          </div>
        </div>
      )}
      {error ? (
        <div
          role="alert"
          style={{
            marginTop: 8,
            padding: '6px 8px',
            borderRadius: 6,
            background: 'hsl(0 65% 55% / 0.08)',
            color: 'hsl(0 65% 40%)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}
